import { useEffect, useState } from "react";
import {
  CheckCircle2,
  FileText,
  Info,
  Lock,
  MessageSquarePlus,
  ShieldCheck,
  Send,
  User,
  Users,
  CalendarRange,
  ListChecks,
  Layers,
} from "lucide-react";
import StatusBadge from "../Program/StatusBadge";
import PrioritasBadge from "../Program/PrioritasBadge";
import HighlightBadge from "./HighlightBadge";
import HistoryTimeline from "./HistoryTimeline";
import {
  computeHighlight,
  computePermissions,
  formatDateShort,
} from "../../utils/progressUtils";
import { hitungJumlahHari } from "../../utils/assignmentStorage";
import { STATUS_LABELS } from "../../data/programData";

function AssignmentInfoCard({ assignment, userById = {} }) {
  const dalnis = assignment?.dalnisId ? userById[assignment.dalnisId] : null;
  const ketua = assignment?.ketuaId ? userById[assignment.ketuaId] : null;
  const anggotas = (assignment?.anggotaIds || [])
    .map((id) => userById[id])
    .filter(Boolean);
  const jumlahHari =
    assignment?.jumlahHari ??
    hitungJumlahHari(assignment?.tanggalMulai, assignment?.tanggalSelesai);
  const subPenugasan = assignment?.subPenugasan || [];

  const hasDurasi = assignment?.tanggalMulai && assignment?.tanggalSelesai;
  const hasAnything =
    dalnis ||
    ketua ||
    anggotas.length > 0 ||
    assignment?.noSt ||
    assignment?.untuk ||
    hasDurasi ||
    subPenugasan.length > 0;

  if (!hasAnything) return null;

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4"
      data-testid="progress-assignment-info"
    >
      <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        Detail Penugasan
      </div>
      <div className="space-y-1.5 text-[12px]">
        {dalnis || ketua || anggotas.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {dalnis ? (
              <span className="inline-flex items-center gap-1 text-slate-700">
                <User className="h-3 w-3 text-slate-400" strokeWidth={1.8} />
                <span className="text-slate-500">Dalnis:</span> {dalnis.name}
              </span>
            ) : null}
            {ketua ? (
              <span className="inline-flex items-center gap-1 text-slate-700">
                <User className="h-3 w-3 text-slate-400" strokeWidth={1.8} />
                <span className="text-slate-500">Ketua:</span> {ketua.name}
              </span>
            ) : null}
            {anggotas.length > 0 ? (
              <span className="inline-flex items-center gap-1 text-slate-700">
                <Users className="h-3 w-3 text-slate-400" strokeWidth={1.8} />
                <span className="text-slate-500">Anggota:</span>{" "}
                {anggotas.map((a) => a.name).join(", ")}
              </span>
            ) : null}
          </div>
        ) : null}

        {assignment?.noSt ? (
          <div className="flex items-center gap-1.5 text-slate-700">
            <FileText className="h-3 w-3 shrink-0 text-slate-400" strokeWidth={1.8} />
            <span className="text-slate-500">No. ST:</span> {assignment.noSt}
          </div>
        ) : null}

        {assignment?.untuk ? (
          <div className="flex items-start gap-1.5 text-slate-700">
            <ListChecks className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" strokeWidth={1.8} />
            <span>
              <span className="text-slate-500">Untuk:</span> {assignment.untuk}
            </span>
          </div>
        ) : null}

        {hasDurasi ? (
          <div className="flex items-center gap-1.5 text-slate-700">
            <CalendarRange className="h-3 w-3 shrink-0 text-slate-400" strokeWidth={1.8} />
            <span className="text-slate-500">Durasi:</span>{" "}
            {formatDateShort(assignment.tanggalMulai)} s.d.{" "}
            {formatDateShort(assignment.tanggalSelesai)}
            {jumlahHari ? ` (${jumlahHari} hari)` : ""}
          </div>
        ) : null}
      </div>

      {subPenugasan.length > 0 ? (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            <Layers className="h-3 w-3" strokeWidth={2} />
            Sub Penugasan ({subPenugasan.length})
          </div>
          <ul className="space-y-1">
            {subPenugasan.map((sp) => {
              const jh = sp.jumlahHari ?? hitungJumlahHari(sp.tanggalMulai, sp.tanggalSelesai);
              const spKetua = sp.ketuaId ? userById[sp.ketuaId] : null;
              const spAnggotas = (sp.anggotaIds || []).map((id) => userById[id]).filter(Boolean);
              return (
                <li key={sp.id} className="text-[12px] text-slate-700">
                  <span className="font-medium">{sp.nama}</span>
                  {sp.tanggalMulai && sp.tanggalSelesai ? (
                    <span className="text-slate-500">
                      {" "}
                      — {formatDateShort(sp.tanggalMulai)} s.d. {formatDateShort(sp.tanggalSelesai)}
                      {jh ? ` (${jh} hari)` : ""}
                    </span>
                  ) : null}
                  {sp.noSt || spKetua || spAnggotas.length > 0 ? (
                    <span className="block text-[11px] text-slate-500">
                      {sp.noSt ? `No. ST: ${sp.noSt}` : ""}
                      {sp.noSt && (spKetua || spAnggotas.length > 0) ? " · " : ""}
                      {spKetua ? `Ketua: ${spKetua.name}` : ""}
                      {spKetua && spAnggotas.length > 0 ? " · " : ""}
                      {spAnggotas.length > 0
                        ? `Anggota: ${spAnggotas.map((a) => a.name).join(", ")}`
                        : ""}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
        {label}
      </div>
      {children}
      {hint ? <div className="mt-1 text-[10.5px] text-slate-500">{hint}</div> : null}
    </label>
  );
}

const inputCls =
  "block w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 disabled:bg-gray-50 disabled:text-slate-500";

export default function ProgressDetailPanel({
  sub,
  user,
  assignment,
  userById = {},
  onInputProgress,
  onVerify,
  onAddKoordinasi,
}) {
  const perms = computePermissions(user, sub, assignment);
  const highlight = computeHighlight(sub);

  // Progress form state
  const [form, setForm] = useState(null);
  // Koordinasi state
  const [catatanKoordinasi, setCatatanKoordinasi] = useState("");
  // Verify state
  const [verifyChecked, setVerifyChecked] = useState(!!sub.verifiedByKetua);

  useEffect(() => {
    setForm({
      progress: sub.progress || 0,
      status: sub.status || "BELUM_MULAI",
      mulaiAktual: sub.mulaiAktual || "",
      selesaiAktual: sub.selesaiAktual || "",
      catatan: sub.catatan || "",
      namaDokumen: sub.namaDokumen || "",
      kma: sub.kma || "",
    });
    setVerifyChecked(!!sub.verifiedByKetua);
    setCatatanKoordinasi("");
  }, [sub.id, sub.progress, sub.status, sub.mulaiAktual, sub.selesaiAktual, sub.catatan, sub.namaDokumen, sub.kma, sub.verifiedByKetua]);

  if (!form) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitProgress = (e) => {
    e.preventDefault();
    if (!perms.canInputProgress) return;
    onInputProgress(sub.id, { ...form }, user);
  };

  const submitKoordinasi = (e) => {
    e.preventDefault();
    if (!perms.canAddKoordinasi) return;
    const txt = catatanKoordinasi.trim();
    if (!txt) return;
    onAddKoordinasi(sub.id, txt, user);
    setCatatanKoordinasi("");
  };

  const submitVerify = (e) => {
    e.preventDefault();
    if (!perms.canVerify) return;
    onVerify(sub.id, verifyChecked, user);
  };

  const verifier = sub.verifiedBy ? userById[sub.verifiedBy] : null;

  return (
    <div className="space-y-5" data-testid="progress-detail">
      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="font-mono">{sub.programKode} · {sub.id}</span>
          {sub.kategori ? (
            <span className="inline-flex h-4 items-center rounded bg-[#e8edf2] px-1 text-[9px] font-bold text-[#1e3a5f]">
              {sub.kategori}
            </span>
          ) : null}
        </div>
        <h2 className="mt-1 font-display text-base font-bold leading-snug tracking-tight text-slate-900">
          {sub.nama}
        </h2>
        <div className="mt-0.5 truncate text-[11.5px] text-slate-500" title={sub.programNama}>
          Program: {sub.programNama}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <StatusBadge status={sub.status} />
          <PrioritasBadge prioritas={sub.prioritas} />
          <HighlightBadge highlight={highlight} />
          {sub.verifiedByKetua ? (
            <span
              data-testid="verified-badge"
              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700"
            >
              <ShieldCheck className="h-3 w-3" strokeWidth={2.2} />
              Diverifikasi Ketua Tim
            </span>
          ) : null}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 text-[11.5px]">
          <div>
            <span className="text-slate-500">Target:</span>{" "}
            <span className="font-medium text-slate-800">
              {formatDateShort(sub.targetMulai)} → {formatDateShort(sub.targetSelesai)}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Aktual:</span>{" "}
            <span className="font-medium text-slate-800">
              {formatDateShort(sub.mulaiAktual)} → {formatDateShort(sub.selesaiAktual)}
            </span>
          </div>
        </div>
      </div>

      {/* Detail Penugasan (Dalnis/Ketua/Anggota, No. ST, Untuk, Durasi, Sub Penugasan) */}
      <AssignmentInfoCard assignment={assignment} userById={userById} />

      {/* Access notice */}
      <div
        data-testid="access-notice"
        className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[11.5px] ${
          perms.readOnly && !perms.canAddKoordinasi
            ? "border-gray-200 bg-gray-50 text-slate-600"
            : "border-emerald-200 bg-emerald-50 text-emerald-800"
        }`}
      >
        {perms.readOnly && !perms.canAddKoordinasi ? (
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        ) : (
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        )}
        <span>
          {perms.reason ||
            (perms.canInputProgress
              ? "Anda dapat menginput progres dan mengubah status sub-kegiatan ini."
              : "")}
        </span>
      </div>

      {/* Progress form (ANGGOTA/KETUA) */}
      {perms.canInputProgress ? (
        <form
          onSubmit={submitProgress}
          data-testid="progress-form"
          className="space-y-4 rounded-lg border border-gray-200 bg-white p-4"
        >
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
            <Send className="h-3.5 w-3.5 text-[#1e3a5f]" />
            Input Progres
          </div>

          <Field label={`Persentase Progres (${form.progress}%)`}>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={form.progress}
              onChange={(e) => setField("progress", Number(e.target.value))}
              data-testid="progress-slider"
              className="w-full accent-[#1e3a5f]"
            />
            {form.progress === 0 && (
              <div className="mt-2 text-xs text-slate-500 italic">
                Belum mulai
              </div>
            )}
          </Field>

          {/* KMA field for P11-04 (Pemeriksaan lanjutan pengaduan masyarakat) */}
          {sub.id === "P11-04" && (
            <Field 
              label="KMA" 
              hint={perms.canVerify ? "Wajib diisi untuk Ketua Tim" : "Dapat diisi oleh Ketua Tim dan Anggota Tim"}
            >
              <input
                type="text"
                value={form.kma}
                onChange={(e) => setField("kma", e.target.value)}
                data-testid="progress-kma"
                placeholder="Masukkan nomor KMA..."
                className={inputCls}
                required={perms.canVerify}
              />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                data-testid="progress-status"
                className={inputCls}
              >
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Nama Dokumen Output">
              <input
                type="text"
                value={form.namaDokumen}
                onChange={(e) => setField("namaDokumen", e.target.value)}
                data-testid="progress-nama-dokumen"
                placeholder="mis. Laporan Reviu Final.pdf"
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal Mulai Aktual">
              <input
                type="date"
                value={form.mulaiAktual}
                onChange={(e) => setField("mulaiAktual", e.target.value)}
                data-testid="progress-mulai-aktual"
                className={inputCls}
              />
            </Field>
            <Field
              label="Tanggal Selesai Aktual"
              hint="Isi saat status ditandai Selesai."
            >
              <input
                type="date"
                value={form.selesaiAktual}
                onChange={(e) => setField("selesaiAktual", e.target.value)}
                data-testid="progress-selesai-aktual"
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Catatan / Kendala">
            <textarea
              value={form.catatan}
              onChange={(e) => setField("catatan", e.target.value)}
              rows={3}
              data-testid="progress-catatan"
              placeholder="Catatan progres, kendala, atau penjelasan status…"
              className={`${inputCls} resize-none`}
            />
          </Field>

          <div className="flex justify-end gap-2">
            <button
              type="submit"
              data-testid="progress-submit"
              className="inline-flex items-center gap-2 rounded-md bg-[#1e3a5f] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#152a46]"
            >
              <Send className="h-3.5 w-3.5" />
              Simpan Progres
            </button>
          </div>
        </form>
      ) : null}

      {/* Verify panel (KETUA) */}
      {perms.canVerify ? (
        <form
          onSubmit={submitVerify}
          data-testid="verify-form"
          className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4"
        >
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
            <ShieldCheck className="h-3.5 w-3.5" />
            Verifikasi Ketua Tim
          </div>
          <label className="flex items-start gap-2 text-[12.5px] text-slate-800">
            <input
              type="checkbox"
              checked={verifyChecked}
              onChange={(e) => setVerifyChecked(e.target.checked)}
              data-testid="verify-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-emerald-600"
            />
            <span>
              <span className="font-semibold">Diverifikasi Ketua Tim</span>
              <span className="block text-[11.5px] text-slate-600">
                Centang sebagai konfirmasi bahwa progres dan output sub-kegiatan telah
                diperiksa Ketua Tim.
              </span>
            </span>
          </label>
          {sub.verifiedByKetua && verifier ? (
            <div className="text-[11px] text-emerald-800">
              Diverifikasi oleh <b>{verifier.name}</b>
            </div>
          ) : null}
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="verify-submit"
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Simpan Verifikasi
            </button>
          </div>
        </form>
      ) : null}

      {/* Koordinasi panel (DALNIS) */}
      {perms.canAddKoordinasi ? (
        <form
          onSubmit={submitKoordinasi}
          data-testid="koordinasi-form"
          className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/40 p-4"
        >
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-800">
            <MessageSquarePlus className="h-3.5 w-3.5" />
            Catatan Koordinasi Dalnis
          </div>
          <textarea
            value={catatanKoordinasi}
            onChange={(e) => setCatatanKoordinasi(e.target.value)}
            rows={3}
            data-testid="koordinasi-textarea"
            placeholder="Catatan koordinasi / arahan untuk tim pelaksana…"
            className={`${inputCls} resize-none`}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="koordinasi-submit"
              disabled={!catatanKoordinasi.trim()}
              className="inline-flex items-center gap-1.5 rounded-md border border-blue-600 bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send className="h-3.5 w-3.5" />
              Tambah Catatan
            </button>
          </div>
        </form>
      ) : null}

      {/* Read-only detail when no edit permission */}
      {perms.readOnly && !perms.canAddKoordinasi ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="progress-readonly">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Detail Progres
          </div>
          <div className="space-y-2 text-[12.5px]">
            <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5">
              <span className="text-slate-500">Progres</span>
              <span className="font-semibold text-slate-900">{sub.progress || 0}%</span>
            </div>
            <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5">
              <span className="text-slate-500">Dokumen Output</span>
              <span className="text-slate-800 flex items-center gap-1">
                {sub.namaDokumen ? (
                  <>
                    <FileText className="h-3 w-3 text-slate-400" />
                    {sub.namaDokumen}
                  </>
                ) : (
                  <span className="italic text-slate-400">—</span>
                )}
              </span>
            </div>
            <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5">
              <span className="text-slate-500">Catatan</span>
              <span className="max-w-[60%] whitespace-pre-wrap text-right text-slate-800">
                {sub.catatan?.trim() || <span className="italic text-slate-400">—</span>}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* History timeline */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Riwayat Progres
          </div>
          <div className="text-[10.5px] text-slate-400">
            {(sub.history || []).length} catatan
          </div>
        </div>
        <HistoryTimeline history={sub.history || []} />
      </div>
    </div>
  );
}
